import type { FC, FormEvent, ReactElement, ReactNode } from 'react'

import { useState } from 'react'
import { Flag, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { trackEvent } from '@/lib/analytics/client'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { NativeSelect, Field } from '@/components/marketplace/form-controls'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@/components/ui/dialog'

export const REPORT_REASONS = [
    { value: 'scam', label: 'Scam or fraud' },
    { value: 'prohibited', label: 'Prohibited or illegal item' },
    { value: 'stolen', label: 'Possibly stolen goods' },
    { value: 'misleading', label: 'Misleading or fake listing' },
    { value: 'duplicate', label: 'Duplicate or spam' },
    { value: 'wrong_category', label: 'Wrong category' },
    { value: 'offensive', label: 'Offensive content' },
    { value: 'other', label: 'Something else' }
]

interface ReportDialogProps {
    listingId: string
    listingTitle: string
    sellerId?: string
    trigger?: ReactElement
}

/**
 * Report flow for anonymous buyers. No account required (spec section 11),
 * so the only identity captured is an optional contact the reporter
 * volunteers, plus a hashed IP recorded server side for rate limiting.
 */
export const ReportDialog: FC<ReportDialogProps> = ({ listingId, listingTitle, sellerId, trigger }): ReactNode => {
    const [open, setOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const form = event.currentTarget
        const data = new FormData(form)

        setSubmitting(true)

        try {
            const response = await fetch('/api/reports', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    listingId,
                    sellerId,
                    reason: String(data.get('reason') ?? 'other'),
                    details: String(data.get('details') ?? ''),
                    contact: String(data.get('contact') ?? '')
                })
            })

            const payload = await response.json().catch(() => null)

            if (!response.ok) throw new Error(payload?.message ?? 'The report could not be sent.')

            trackEvent({ type: 'report_submitted', listingId, sellerId })
            toast.success('Report sent', { description: 'A moderator will review this listing. Thank you.' })

            setOpen(false)
            form.reset()
        } catch (error) {
            toast.error('Report not sent', {
                description: error instanceof Error ? error.message : 'Please try again in a moment.'
            })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger
                render={trigger ?? (
                    <Button variant='ghost' className='h-10 px-3 text-sm text-muted-foreground hover:text-destructive' />
                )}
            >
                {trigger ? undefined : (
                    <>
                        <Flag className='size-4' aria-hidden='true' />
                        Report listing
                    </>
                )}
            </DialogTrigger>

            <DialogContent className='max-w-md'>
                <DialogHeader>
                    <DialogTitle>
                        Report this listing
                    </DialogTitle>

                    <DialogDescription>
                        {`Tell us what is wrong with "${listingTitle}". Reports go straight to the moderation queue.`}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className='flex flex-col gap-4 p-4 pt-0'>
                    <Field label='Reason' htmlFor='report-reason' required>
                        <NativeSelect id='report-reason' name='reason' defaultValue='scam' required>
                            {REPORT_REASONS.map((reason) => (
                                <option key={reason.value} value={reason.value}>
                                    {reason.label}
                                </option>
                            ))}
                        </NativeSelect>
                    </Field>

                    <Field label='What happened' htmlFor='report-details' hint='Optional, but details help a moderator act faster.'>
                        <Textarea
                            id='report-details'
                            name='details'
                            rows={4}
                            maxLength={1000}
                            placeholder='Describe the problem'
                            className='min-h-24 resize-y'
                        />
                    </Field>

                    <Field label='Your contact' htmlFor='report-contact' hint='Optional. A Telegram username or email if you are happy to be contacted.'>
                        <input
                            id='report-contact'
                            name='contact'
                            maxLength={120}
                            placeholder='@username'
                            className='h-11 rounded-lg border border-input bg-card px-3 text-sm outline-none focus-visible:border-primary/60 focus-visible:ring-3 focus-visible:ring-primary/20'
                        />
                    </Field>

                    <div className='flex gap-2'>
                        <Button type='submit' disabled={submitting} className='h-11 flex-1 font-semibold'>
                            {submitting && <Loader2 className='size-4 animate-spin' aria-hidden='true' />}
                            {submitting ? 'Sending' : 'Send report'}
                        </Button>

                        <Button
                            type='button'
                            variant='outline'
                            onClick={() => setOpen(false)}
                            disabled={submitting}
                            className='h-11 px-4'
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}
